import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../api/client";


const CLASSIFICATION_LABELS = {
  INCOME_REAL_ESTATE: "נדל״ן מניב",
  CONSTRUCTION_FINANCE: "ליווי בנייה",
  LAND_LOAN: "הלוואת קרקע",
  BUSINESS_CREDIT: "אשראי עסקי",
  LOAN: "הלוואה",
  CREDIT_FACILITY: "מסגרת אשראי",
  OTHER: "אחר",
};

function formatM(value) {
  if (value == null || value === "") return "";
  const num = Number(value);
  if (Number.isNaN(num)) return "";
  const abs = Math.abs(num);
  if (abs >= 1_000_000) return `₪${parseFloat((num / 1_000_000).toFixed(1))}M`;
  if (abs >= 1_000) return `₪${parseFloat((num / 1_000).toFixed(0))}K`;
  return `₪${num.toLocaleString("he-IL")}`;
}

export default function GlobalSearch({ open, onClose }) {
  const [query, setQuery] = useState("");
  const [allLoans, setAllLoans] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const navigate = useNavigate();

  // Fetch loans once when opened
  useEffect(() => {
    if (!open) return;
    apiClient.get(`/loans`).then((res) => {
      setAllLoans(Array.isArray(res.data) ? res.data : []);
    }).catch(() => {});
  }, [open]);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Filter results
  const results = React.useMemo(() => {
    if (!query.trim()) return allLoans.slice(0, 8);
    const q = query.trim().toLowerCase();
    return allLoans
      .filter(
        (l) =>
          (l.lender || "").toLowerCase().includes(q) ||
          (l.borrowerCompany || "").toLowerCase().includes(q) ||
          (l.projectName || "").toLowerCase().includes(q) ||
          (l.basketName || "").toLowerCase().includes(q)
      )
      .slice(0, 10);
  }, [query, allLoans]);

  // Reset active index when results change
  useEffect(() => { setActiveIndex(0); }, [results]);

  const handleSelect = useCallback((loan) => {
    onClose();
    navigate(`/loan/${loan._id}`);
  }, [navigate, onClose]);

  const handleKeyDown = (e) => {
    if (e.key === "Escape") { onClose(); return; }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && results[activeIndex]) {
      handleSelect(results[activeIndex]);
    }
  };

  // Scroll active item into view
  useEffect(() => {
    if (!listRef.current) return;
    const item = listRef.current.children[activeIndex];
    item?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  if (!open) return null;

  return (
    <div className="gs-overlay" onClick={onClose}>
      <div className="gs-modal" onClick={(e) => e.stopPropagation()} onKeyDown={handleKeyDown}>
        <div className="gs-input-wrap">
          <svg className="gs-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="6.5" cy="6.5" r="5" stroke="currentColor" strokeWidth="1.6"/>
            <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
          </svg>
          <input
            ref={inputRef}
            type="text"
            className="gs-input"
            placeholder="חפש הלוואה — מלווה, לווה, פרויקט..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <kbd className="gs-esc-hint">Esc</kbd>
        </div>

        {results.length > 0 ? (
          <ul className="gs-list" ref={listRef} role="listbox">
            {results.map((loan, i) => (
              <li
                key={loan._id}
                role="option"
                aria-selected={i === activeIndex}
                className={`gs-item${i === activeIndex ? " gs-item-active" : ""}`}
                onMouseEnter={() => setActiveIndex(i)}
                onClick={() => handleSelect(loan)}
              >
                <div className="gs-item-main">
                  <span className="gs-item-lender">{loan.lender || "—"}</span>
                  <span className="gs-item-arrow">←</span>
                  <span className="gs-item-borrower">{loan.borrowerCompany || "—"}</span>
                </div>
                <div className="gs-item-meta">
                  {loan.projectName && <span className="gs-meta-chip">{loan.projectName}</span>}
                  {loan.classification && (
                    <span className="gs-meta-chip">
                      {CLASSIFICATION_LABELS[loan.classification] || loan.classification}
                    </span>
                  )}
                  <span className="gs-meta-amount">{formatM(loan.remainingPrincipal ?? loan.principal)}</span>
                </div>
              </li>
            ))}
          </ul>
        ) : query.trim() ? (
          <div className="gs-empty">לא נמצאו תוצאות עבור &quot;{query}&quot;</div>
        ) : null}

        <div className="gs-footer">
          <span className="gs-hint">↑↓ ניווט</span>
          <span className="gs-hint">↵ בחר</span>
          <span className="gs-hint">Esc סגור</span>
        </div>
      </div>
    </div>
  );
}
